// Chart-color helpers for SABER charts.
// Follows the GLOBAL light/dark theme: ThemeToggle toggles the `dark` class on
// document.documentElement. These helpers read that class at call time so charts
// match whichever theme is active. NOT forced-dark.

export interface SaberChartTheme {
  /** Categorical series palette (status-agnostic). */
  series: string[]
  /** Grid / axis line color. */
  grid: string
  /** Axis tick / label text color. */
  axis: string
  /** Tooltip surface background. */
  tooltipBg: string
  /** Tooltip border color. */
  tooltipBorder: string
  /** Tooltip text color. */
  tooltipText: string
  /** Semantic status colors (OK / WARN / CRITICAL). */
  ok: string
  warn: string
  critical: string
}

/** True when the global dark theme is active (`.dark` on <html>). */
export const isDarkTheme = (): boolean =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('dark')

const LIGHT_THEME: SaberChartTheme = {
  series: ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6'],
  grid: '#e2e8f0',
  axis: '#475569',
  tooltipBg: '#ffffff',
  tooltipBorder: '#e2e8f0',
  tooltipText: '#0f172a',
  ok: '#16a34a',
  warn: '#d97706',
  critical: '#dc2626',
}

const DARK_THEME: SaberChartTheme = {
  series: ['#818cf8', '#38bdf8', '#34d399', '#fbbf24', '#f472b6', '#a78bfa'],
  grid: '#334155',
  axis: '#94a3b8',
  tooltipBg: '#1e293b',
  tooltipBorder: '#334155',
  tooltipText: '#f1f5f9',
  ok: '#4ade80',
  warn: '#fbbf24',
  critical: '#f87171',
}

/** Resolve the chart theme for the currently active global theme. */
export const getSaberChartTheme = (): SaberChartTheme => (isDarkTheme() ? DARK_THEME : LIGHT_THEME)

/** Pick a categorical series color by index (wraps around the palette). */
export const seriesColor = (index: number, theme: SaberChartTheme = getSaberChartTheme()): string =>
  theme.series[((index % theme.series.length) + theme.series.length) % theme.series.length]

/** Darken a #rrggbb hex color by mixing toward black (amount 0–1). */
export const darkenHex = (hex: string, amount: number): string => {
  const n = Number.parseInt(hex.slice(1), 16)
  if (Number.isNaN(n)) return hex
  const mix = (c: number): number => Math.round(c * (1 - amount))
  const r = mix((n >> 16) & 0xff)
  const g = mix((n >> 8) & 0xff)
  const b = mix(n & 0xff)
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}
